import { ComponentStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

import { COLORS } from '../../theme';

import { TableExperimentalProps } from './TableExperimental';

const TableExperimentalTheme: ComponentStyleConfig = {
  parts: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'tfoot', 'caption'],
  baseStyle: (props: TableExperimentalProps & StyleFunctionProps) => {
    const {
      disableRowHoverStyle,
      isStickyHeader,
      isStickyFirstColumn,
      isScrollLeftEdge,
      onRowClick,
      pagination
    } = props;

    return {
      tableContainer: {
        overflowX: 'auto'
      },
      table: {
        tableLayout: 'fixed'
      },
      thead: {
        ...(isStickyHeader && {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          _light: {
            boxShadow: `0 1px 0 0 ${COLORS.silver.base}`
          },
          _dark: {
            boxShadow: `0 1px 0 0 ${COLORS.black.lightest}`
          }
        }),
        tr: {
          borderBottomWidth: !isStickyHeader ? '1px' : '0px',
          _light: {
            borderColor: 'silver.base'
          },
          _dark: {
            borderColor: 'black.lightest'
          },
          th: {
            _first: {
              borderRadius: 0
            },
            _last: {
              borderRadius: 0
            },

            '&:first-of-type': {
              zIndex: 1
            }
          }
        }
      },
      th: {
        padding: '0',
        textTransform: 'unset',

        '&[data-is-numeric=true]': {
          textAlign: 'right'
        },

        ...(isStickyFirstColumn &&
          !isScrollLeftEdge && {
            '&:first-of-type': {
              position: 'sticky',
              left: 0,

              '&::after': {
                content: '""',
                height: '100%',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                pointerEvents: 'none',
                borderRightWidth: '1px'
              }
            }
          }),

        _light: {
          color: 'grey.base',
          backgroundColor: 'silver.lightest',

          button: {
            color: 'grey.base'
          },

          ...(isStickyFirstColumn &&
            !isScrollLeftEdge && {
              '&:first-of-type': {
                '&::after': {
                  borderRightColor: COLORS.silver.base
                }
              }
            })
        },
        _dark: {
          color: 'grey.lightest',
          backgroundColor: 'black.base',

          button: {
            color: 'grey.lightest'
          },

          ...(isStickyFirstColumn &&
            !isScrollLeftEdge && {
              '&:first-of-type': {
                '&::after': {
                  borderRightColor: COLORS.black.lightest
                }
              }
            })
        },

        tr: {
          borderBottomWidth: 0
        }
      },
      thContainer: {
        minHeight: '40px',
        padding: '8px',
        width: '100%',
        height: '100%',
        borderRadius: '0',
        display: 'flex',
        alignItems: 'center',
        letterSpacing: 'normal',

        _focus: {
          _light: {
            boxShadow: `inset 0px 0px 0px 1px ${COLORS.silver.lightest}, inset 0px 0px 0px 4px ${COLORS.blue.lighter}`
          },
          _dark: {
            boxShadow: `inset 0px 0px 0px 1px ${COLORS.black.base}, inset 0px 0px 0px 4px ${COLORS.blue.lighter}`
          }
        }
      },
      tbody: {
        _hover: {
          cursor: onRowClick ? 'pointer' : 'default'
        },
        tr: {
          borderBottomWidth: '1px',

          ...(!disableRowHoverStyle && {
            _hover: {
              _light: {
                'td:not([aria-busy="true"])': {
                  borderTop: `1px solid ${COLORS.silver.lightest}`,
                  backgroundColor: '#f6f6f6',
                  borderBottom: `1px solid ${COLORS.silver.lightest}`
                }
              },
              _dark: {
                td: {
                  borderTop: `1px solid ${COLORS.black.base}`,
                  borderBottom: `1px solid ${COLORS.black.base}`,
                  backgroundColor: '#25282c'
                }
              },

              '&:first-of-type': {
                td: {
                  ...(isStickyHeader && {
                    _light: {
                      borderTop: 0
                    }
                  })
                }
              },

              '&:last-child': {
                _hover: {
                  td: {
                    borderBottomWidth: `0px`
                  }
                }
              }
            }
          }),

          ...(onRowClick && {
            _active: {
              _light: {
                backgroundColor: '#f1f1f1'
              },
              _dark: {
                backgroundColor: '#383a3f'
              }
            }
          }),

          [`&:last-child`]: {
            _light: {
              borderBottomWidth: `1px`,
              borderBottomColor: pagination ? 'inherit' : 'transparent'
            },
            _dark: {
              borderBottomWidth: `1px`,
              borderBottomColor: pagination ? 'inherit' : 'transparent'
            },
            _hover: {
              borderColor: 'transparent'
            }
          }
        }
      },
      tr: {
        _light: {
          borderColor: 'silver.base',
          backgroundColor: 'silver.lightest'
        },
        _dark: {
          borderColor: 'black.lightest',
          backgroundColor: 'black.base'
        },
        td: {
          [`&:first-of-type`]: {
            borderRadius: '8px 0px 0px 8px',
            fontWeight: 'medium'
          },
          [`&:last-child`]: {
            borderRadius: '0px 8px 8px 0px'
          }
        },
        '&[data-is-selected=true]': {
          td: {
            _light: {
              borderTop: `1px solid ${COLORS.silver.lightest}`,
              borderBottom: `1px solid ${COLORS.silver.lightest}`,
              backgroundColor: 'blue.lightest'
            },
            _dark: {
              borderTop: `1px solid ${COLORS.black.base}`,
              borderBottom: `1px solid ${COLORS.black.base}`,
              backgroundColor: 'blue.darkest'
            }
          }
        }
      },
      td: {
        padding: '16px 8px',
        backgroundColor: 'inherit',
        verticalAlign: 'top',

        '&[data-is-numeric=true]': {
          textAlign: 'right'
        },

        ...(isStickyFirstColumn &&
          !isScrollLeftEdge && {
            '&:first-of-type': {
              position: 'sticky',
              left: 0,

              '&::after': {
                content: '""',
                height: '100%',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                pointerEvents: 'none',
                borderRightWidth: '1px'
              }
            }
          }),

        _light: {
          color: 'black.lighter',

          ...(isStickyFirstColumn &&
            !isScrollLeftEdge && {
              '&:first-of-type': {
                '&::after': {
                  borderRightColor: COLORS.silver.base
                }
              }
            })
        },
        _dark: {
          color: 'silver.lighter',

          ...(isStickyFirstColumn &&
            !isScrollLeftEdge && {
              '&:first-of-type': {
                '&::after': {
                  borderRightColor: COLORS.black.lightest
                }
              }
            })
        }
      },
      thCheckbox: {
        margin: '12px 8px'
      },
      tdCheckbox: {
        marginTop: '3px',
        marginRight: '16px'
      },
      dash: {
        _light: {
          color: 'grey.base'
        },
        _dark: {
          color: 'grey.lightest'
        }
      },
      pagination: {
        height: '56px',
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        '> div': {
          marginRight: '8px',
          select: {
            paddingLeft: '12px',
            paddingRight: '24px',
            boxSizing: 'content-box'
          }
        },
        '> button': {
          svg: { height: '12px' }
        },
        '.chakra-select__icon-wrapper': {
          width: 'auto',
          right: '10px'
        }
      },
      loading: {
        td: {
          padding: '12px 8px',
          cursor: 'default'
        }
      }
    };
  },
  sizes: {
    md: {
      th: {
        fontSize: 'sm',
        fontWeight: 'medium'
      },
      thread: {
        tr: {
          height: '40px'
        }
      },
      td: {
        fontSize: 'md'
      }
    }
  },
  variants: {},
  defaultProps: {
    variant: 'unstyled',
    size: 'md',
    colorScheme: undefined
  }
};

export default TableExperimentalTheme;
